import { useEffect, useState } from "react";
import { MapPin, ArrowUpRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { SignupForm } from "@/components/Forms";
import { useSite } from "@/lib/SiteContext";
import { api } from "@/lib/api";

function EventCard({ ev, past = false }) {
  let dateLabel = ev.date;
  try {
    dateLabel = format(parseISO(ev.date), "MMMM d, yyyy");
  } catch (_) { /* keep raw */ }
  return (
    <div className={`py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 ${past ? "opacity-50" : ""}`} data-testid={`event-${ev.event_id}`}>
      <div>
        <p className="font-ui text-xs uppercase tracking-[0.25em] text-crimsonhi mb-2">
          {dateLabel}{ev.time ? ` · ${ev.time}` : ""}{ev.timezone ? ` ${ev.timezone}` : ""}
        </p>
        <h3 className="font-display text-2xl sm:text-3xl text-paper">{ev.title}</h3>
        {(ev.venue || ev.city) && (
          <p className="inline-flex items-center gap-2 font-ui text-sm text-paper/50 mt-2">
            <MapPin size={14} /> {[ev.venue, ev.city].filter(Boolean).join(", ")}
          </p>
        )}
        {ev.description && <p className="font-reading text-lg text-paper/60 mt-3 max-w-xl">{ev.description}</p>}
      </div>
      {ev.url && !past && (
        <a href={ev.url} target="_blank" rel="noopener noreferrer" data-testid={`event-register-${ev.event_id}`}
          className="shrink-0 inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.22em] font-semibold border border-paper/25 text-paper px-6 py-3.5 rounded-full transition-[border-color,background-color] duration-300 hover:border-crimson hover:bg-crimson">
          Register <ArrowUpRight size={14} />
        </a>
      )}
    </div>
  );
}

export default function Appearances() {
  const { settings } = useSite();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/public/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (events || []).filter((e) => e.date >= today);
  const past = (events || []).filter((e) => e.date < today).reverse();

  return (
    <div className="bg-ink min-h-screen" data-testid="appearances-page">
      <Seo title="Appearances — Dirty Church Chronicles"
        description="Confirmed book signings, speaking engagements, and launches for Dirty Church Chronicles."
        path="/appearances" />
      <section className="pt-40 pb-24 max-w-5xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Appearances</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4">
            Gatherings & Signings
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl mb-16">
            Confirmed dates with {settings.author_name || "Jermaine Miller"}. Only official engagements are listed here.
          </p>
        </Reveal>

        {events !== null && upcoming.length === 0 && (
          <Reveal>
            <div className="border border-paper/10 bg-coal p-10 sm:p-14" data-testid="appearances-empty">
              <h2 className="font-display text-2xl sm:text-3xl text-paper mb-4">Dates are being prepared.</h2>
              <p className="font-ui text-sm text-paper/50 mb-10 max-w-md">
                New appearances are announced to the reader list first. Join below and you will hear as soon as
                dates are confirmed.
              </p>
              <SignupForm />
            </div>
          </Reveal>
        )}

        {upcoming.length > 0 && (
          <div className="divide-y divide-paper/10 border-y border-paper/10" data-testid="upcoming-events">
            {upcoming.map((ev) => <EventCard key={ev.event_id} ev={ev} />)}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-20">
            <p className="eyebrow mb-6">Past Gatherings</p>
            <div className="divide-y divide-paper/10 border-y border-paper/10" data-testid="past-events">
              {past.map((ev) => <EventCard key={ev.event_id} ev={ev} past />)}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
